import { ActionType } from "typesafe-actions";
import { isSameDay, addMilliseconds, min } from "date-fns";

import uid from "short-uuid";
import { AppState } from "./types";
import actions from "./actions";
import { merge } from "lodash/fp";

function timesheetReducer(
  state: AppState,
  action: ActionType<typeof actions>
): AppState {
  try {
    switch (action.type) {
      case "CREATE":
        return createEntry(state, action);

      case "UPDATE":
        return updateEntry(state, action);

      case "REMOVE":
        return removeEntry(state, action);

      case "IMPORT":
        return importEntries(state, action);

      case "LOAD":
        return { ...state, ...action.payload };

      case "CREATE_SHEET":
        return createSheet(state, action);

      case "CHANGE_SHEET":
        const { sheetName, entries } = action.payload;
        return {
          ...state,
          entries,
          sheets: changeSheet(state.sheets, sheetName),
        };

      case "REMOVE_SHEET":
        return {
          ...state,
          sheets: state.sheets.filter((s) => s !== action.payload.sheetName),
        };

      case "RENAME_SHEET":
        return {
          ...state,
          sheets: [action.payload, ...state.sheets.slice(1)],
        };
    }
  } catch (err) {
    return { ...state, error: err.message };
  }

  return state;
}

function createEntry(
  state: AppState,
  action: ActionType<typeof actions.create>
) {
  const { entries } = state;
  const { start, duration, description } = action.payload;

  // Makes no sense!
  // if (!isSameDay(start, addMilliseconds(start, duration))) {
  //   return { ...state, error: "entry spans more than one day" };
  // }

  // when the duration is negative, start will become the end of the entry
  const normalizedStart = min([start, addMilliseconds(start, duration)]);

  return {
    ...state,
    entries: [
      ...entries,
      {
        id: uid.generate(),
        start: normalizedStart,
        duration: Math.abs(duration),
        description,
      },
    ],
  };
}

function updateEntry(
  state: AppState,
  action: ActionType<typeof actions.update>
) {
  const { entries } = state;
  const { entry, updatedEntry } = action.payload;

  if (!entries.some((e) => e.id === entry.id)) {
    throw new Error("entry not found");
  }

  return {
    ...state,
    entries: entries.map((e) =>
      e.id === entry.id ? { ...e, ...updatedEntry } : e
    ),
  };
}

function removeEntry(
  state: AppState,
  action: ActionType<typeof actions.remove>
) {
  const { entries } = state;
  const entry = action.payload;

  return {
    ...state,
    entries: entries.filter((e) => e.id !== entry.id),
  };
}

function importEntries(
  state: AppState,
  action: ActionType<typeof actions.import>
): AppState {
  const { sheets, entries } = state;
  const { newSheet, sheetName, entries: newEntries } = action.payload;

  const importedEntries = newEntries.map((entry) =>
    merge({ id: uid.generate() }, entry)
  );

  if (newSheet) {
    const nextSheetName = uniqueSheetName(sheets, sheetName);
    const nextSheets = changeSheet(
      addSheet(sheets, nextSheetName),
      nextSheetName
    );

    return {
      ...state,
      sheets: nextSheets,
      entries: importedEntries,
      latestImport: newEntries,
    };
  } else {
    return {
      ...state,
      sheets,
      entries: entries.concat(importedEntries),
      latestImport: newEntries,
    };
  }
}

function createSheet(
  state: AppState,
  action: ActionType<typeof actions.createSheet>
) {
  const { sheets } = state;
  const { sheetName } = action.payload;

  return {
    ...state,
    sheets: addSheet(sheets, uniqueSheetName(sheets, sheetName)),
  };
}

function addSheet(sheets: string[], sheetName: string): string[] {
  return [...sheets, sheetName];
}

function changeSheet(sheets: string[], sheetName: string) {
  return sheets.includes(sheetName)
    ? [sheetName, ...sheets.filter((s) => s !== sheetName)]
    : sheets;
}

function uniqueSheetName(sheets: string[], sheetName?: string) {
  const baseName = sheetName || "Unbenannt";
  let newSheetName = baseName;
  for (let i = 1; i < 100 && sheets.includes(newSheetName); i++) {
    newSheetName = `${baseName}-${i}`;
  }
  return newSheetName;
}

export default timesheetReducer;
